import { MediaItem } from "@/lib/media/shared";
import { useEffect, useRef } from "react";
import { Message } from "../../../../lib/chat-store/types";
import { toast } from "react-toastify";
import { env } from "@/lib/env";

export function useMediaPolling(
  messages: Message[],
  conversationId: string | null,
  updateMessages: (
    conversationId: string,
    fn: (prev: Message[]) => Message[],
  ) => void,
) {
  const processingIdsRef = useRef<Set<string>>(new Set());
  const isRunningRef = useRef(false);

  useEffect(() => {
    const ids = new Set<string>();
    for (const msg of messages) {
      if (msg.type !== "media") continue;

      const media = msg.payload?.media || {};

      for (const [id, m] of Object.entries(media)) {
        if (m.Status === "UPLOADED" || m.Status === "PROCESSING") {
          ids.add(id);
        }
      }
    }
    processingIdsRef.current = ids;
  }, [messages]);

  useEffect(() => {
    if (!conversationId) return;
    let isActive = true;

    const run = async () => {
      if (!isActive) return;

      if (processingIdsRef.current.size === 0) {
        return; // ⛔ nothing to do
      }

      if (isRunningRef.current) {
        scheduleNext();
        return;
      }

      isRunningRef.current = true;

      try {
        const ids = Array.from(processingIdsRef.current);

        if (ids.length > 0) {
          const updated = await fetchMediaStatus(ids);

          if (updated.length > 0) {
            updateMessages(conversationId, (prev) => mergeMedia(prev, updated));
          }

          // 🔥 remove finished items
          for (const m of updated) {
            if (
              m.Status === "READY" ||
              m.Status === "FAILED" ||
              m.Status === "REJECTED"
            ) {
              processingIdsRef.current.delete(m.MediaId);
            }
          }
        }
      } catch {
        // handled already
      } finally {
        isRunningRef.current = false;
        scheduleNext();
      }
    };

    const scheduleNext = () => {
      if (!isActive) return;

      if (processingIdsRef.current.size === 0) {
        return; // ⛔ stop loop
      }

      setTimeout(run, 3000);
    };

    // 🚀 START POLLING when messages change
    if (processingIdsRef.current.size > 0) {
      run();
    }

    return () => {
      isActive = false;
    };
  }, [messages, conversationId]);
}

async function fetchMediaStatus(mediaIds: string[]) {
  console.log(
    "called fetch status",
    `${env.NEXT_SHARED_API_URL}/getMediaStatus`,
  );
  const res = await fetch(`/api/media/getMediaStatus`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ MediaIds: mediaIds }),
  });

  const text = await res.text();

  let data: any;

  try {
    data = JSON.parse(text);
    console.log("data", data);
  } catch {
    toast("Invalid server response", { type: "error" });
    throw new Error("INVALID_JSON");
  }

  if (!res.ok) {
    toast(data?.message || "Something went wrong", { type: "error" });
    throw new Error("API_ERROR");
  }

  return data.media as MediaItem[];
}

function mergeMedia(messages: Message[], updates: MediaItem[]): Message[] {
  if (!updates.length) return messages;

  const map = new Map(updates.map((m) => [m.MediaId, m]));

  let changed = false;

  const next = messages.map((msg) => {
    if (msg.type !== "media") return msg;

    const media = msg.payload?.media;
    if (!media) return msg;

    let mediaChanged = false;

    const nextMedia: typeof media = {};

    for (const [id, m] of Object.entries(media)) {
      const updated = map.get(id);

      if (updated && updated.Status !== m.Status) {
        mediaChanged = true;
        nextMedia[id] = { ...m, ...updated };
      } else {
        nextMedia[id] = m;
      }
    }

    if (!mediaChanged) return msg;

    changed = true;

    return {
      ...msg,
      payload: {
        ...msg.payload,
        media: nextMedia,
      },
    };
  });

  return changed ? next : messages;
}
